import React, { useEffect, useState } from "react";
import {
  FilePlus, MapPin, Coins, HandCoins, Bank, Bell, ChartBar, CheckCircle, Buildings,
} from "@phosphor-icons/react";

const SCENES = [
  {
    id: "submit",
    eyebrow: "1 · Originate",
    title: "Submit a loan in 6 guided steps",
    blurb: "Pre-validated fields, smart calculators, and one-click PDF packets. Brokers go from intake to ready-to-fund without touching paper.",
    Icon: FilePlus,
    visual: "submit",
  },
  {
    id: "property",
    eyebrow: "2 · Underwrite",
    title: "Property intelligence on autopilot",
    blurb: "Auto-fetched Google Street View, instant LTV, debt service & cash-to-close calculators on every deal.",
    Icon: MapPin,
    visual: "property",
  },
  {
    id: "syndication",
    eyebrow: "3 · Syndicate",
    title: "Investor syndication portal",
    blurb: "Open a deal to your investor network. Click-to-sign binding commitments fill the cap table to 100%.",
    Icon: Coins,
    visual: "syndication",
  },
  {
    id: "lender",
    eyebrow: "4 · Place",
    title: "Match deals to the right lender",
    blurb: "Score every active lender against loan type, size range, and state footprint — instantly.",
    Icon: Bank,
    visual: "lender",
  },
  {
    id: "commission",
    eyebrow: "5 · Get paid",
    title: "Commission payouts, end to end",
    blurb: "Points-based calculation, MTD / YTD analytics, ACH / wire bundles with one-click CSV export.",
    Icon: HandCoins,
    visual: "commission",
  },
];

const SCENE_DURATION = 5500; // ms

export default function FeatureShowcase() {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const t = setInterval(() => {
      setActive((i) => (i + 1) % SCENES.length);
    }, SCENE_DURATION);
    return () => clearInterval(t);
  }, []);

  const scene = SCENES[active];
  const Icon = scene.Icon;

  return (
    <div
      className="relative overflow-hidden h-full"
      style={{
        background:
          "linear-gradient(155deg, #0E1525 0%, #142243 45%, #1F5DC6 110%)",
      }}
      data-testid="feature-showcase"
    >
      {/* Decorative grid */}
      <div
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.06) 1px, transparent 1px)",
          backgroundSize: "44px 44px",
          maskImage: "radial-gradient(ellipse at 30% 20%, black 30%, transparent 75%)",
          WebkitMaskImage: "radial-gradient(ellipse at 30% 20%, black 30%, transparent 75%)",
        }}
      />

      {/* Soft glow */}
      <div
        className="absolute"
        style={{
          width: 700,
          height: 700,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(64,134,239,0.35), transparent 60%)",
          top: -200,
          right: -200,
          filter: "blur(40px)",
        }}
      />

      <div className="relative h-full flex flex-col justify-between p-10 lg:p-14 text-white">
        {/* Top row: brand mark */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em]" style={{ color: "#FFFFFF" }}>
            <span
              className="inline-block w-2 h-2 rounded-full"
              style={{ background: "#4086EF", boxShadow: "0 0 12px #4086EF" }}
            />
            The Alternative Lending Source
          </div>
          <div className="text-xs uppercase tracking-[0.25em]" style={{ color: "#FFFFFF" }}>
            {String(active + 1).padStart(2, "0")} / {String(SCENES.length).padStart(2, "0")}
          </div>
        </div>

        {/* Center: animated visual */}
        <div className="flex-1 flex items-center justify-center my-6">
          <div
            key={scene.id}
            className="w-full max-w-[520px] animate-scene-in"
            data-testid={`scene-${scene.id}`}
          >
            <SceneVisual which={scene.visual} />
          </div>
        </div>

        {/* Bottom: copy block */}
        <div key={`copy-${scene.id}`} className="animate-fade-up">
          <div className="flex items-center gap-2 mb-3">
            <Icon size={18} weight="duotone" color="#4086EF" />
            <p className="text-xs uppercase tracking-[0.25em]" style={{ color: "#FFFFFF" }}>
              {scene.eyebrow}
            </p>
          </div>
          <h2 className="font-display text-3xl lg:text-4xl font-light tracking-tight mb-3 max-w-xl" style={{ color: "#FFFFFF" }}>
            {scene.title}
          </h2>
          <p className="text-sm lg:text-base max-w-xl leading-relaxed" style={{ color: "#FFFFFF" }}>
            {scene.blurb}
          </p>

          {/* Progress dots / scrubber */}
          <div className="flex items-center gap-2 mt-7">
            {SCENES.map((s, i) => {
              const isActive = i === active;
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setActive(i)}
                  className="relative h-1 rounded-full overflow-hidden transition-all"
                  style={{
                    width: isActive ? 56 : 18,
                    background: "rgba(255,255,255,0.2)",
                  }}
                  aria-label={`Show ${s.title}`}
                  data-testid={`scene-pill-${s.id}`}
                >
                  {isActive && (
                    <span
                      key={`bar-${s.id}-${active}`}
                      className="absolute inset-0 origin-left animate-scene-progress"
                      style={{ background: "#4086EF" }}
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Animations */}
      <style>{`
        @keyframes scene-progress {
          from { transform: scaleX(0); }
          to   { transform: scaleX(1); }
        }
        .animate-scene-progress {
          animation: scene-progress ${SCENE_DURATION}ms linear forwards;
        }
        @keyframes scene-in {
          0%   { opacity: 0; transform: translateY(14px) scale(0.99); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        .animate-scene-in {
          animation: scene-in 700ms cubic-bezier(0.22, 0.61, 0.36, 1) both;
        }
        @keyframes pulse-ring {
          0%   { transform: scale(0.9); opacity: 0.7; }
          100% { transform: scale(1.6); opacity: 0; }
        }
        @keyframes fill-bar {
          from { width: 0; }
          to   { width: var(--target-w, 100%); }
        }
        @keyframes step-progress {
          0%   { width: 0; }
          100% { width: 100%; }
        }
        @keyframes blink {
          0%, 50%, 100% { opacity: 1; }
          25%, 75% { opacity: 0.4; }
        }
        .animate-blink { animation: blink 1.4s ease-in-out infinite; }
      `}</style>
    </div>
  );
}

function SceneVisual({ which }) {
  if (which === "submit") return <SubmitScene />;
  if (which === "property") return <PropertyScene />;
  if (which === "syndication") return <SyndicationScene />;
  if (which === "lender") return <LenderScene />;
  if (which === "commission") return <CommissionScene />;
  return null;
}

function Card({ children, style, className = "" }) {
  return (
    <div
      className={`rounded-lg ${className}`}
      style={{
        background: "rgba(255,255,255,0.96)",
        color: "#0E1525",
        boxShadow: "0 30px 60px -12px rgba(0,0,0,0.4)",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function StepRow({ label, idx, current }) {
  const done = idx < current;
  const active = idx === current;
  return (
    <div className="flex items-center gap-3 py-1.5">
      <div
        className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-semibold transition-colors"
        style={{
          background: done ? "#1E7D50" : active ? "#1F5DC6" : "#E2E5EE",
          color: done || active ? "#FFFFFF" : "#5A6478",
        }}
      >
        {done ? "✓" : idx + 1}
      </div>
      <div className="text-sm" style={{ fontWeight: active ? 600 : 400, color: done || active ? "#0E1525" : "#5A6478" }}>
        {label}
      </div>
      {active && (
        <div className="ml-auto w-16 h-1 rounded overflow-hidden" style={{ background: "#E7EEFB" }}>
          <div
            className="h-full"
            style={{
              background: "#1F5DC6",
              animation: "step-progress 1.2s ease-in-out forwards",
            }}
          />
        </div>
      )}
    </div>
  );
}

function SubmitScene() {
  const [step, setStep] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setStep((s) => (s >= 5 ? 0 : s + 1)), 900);
    return () => clearInterval(t);
  }, []);
  return (
    <Card style={{ padding: 28 }}>
      <div className="flex items-center justify-between mb-5">
        <div>
          <p className="text-[10px] uppercase tracking-[0.25em]" style={{ color: "#5A6478" }}>New loan request</p>
          <p className="font-display text-xl mt-1" style={{ fontWeight: 400 }}>LN-2026-0142</p>
        </div>
        <div className="text-xs px-3 py-1 rounded-full" style={{ background: "#FFF4E0", color: "#A05E10" }}>
          DRAFT
        </div>
      </div>
      {["Loan Type", "Borrower", "Business", "Loan", "Property", "Review"].map((label, i) => (
        <StepRow key={label} label={label} idx={i} current={step} />
      ))}
    </Card>
  );
}

function PropertyScene() {
  return (
    <Card style={{ padding: 0, overflow: "hidden" }}>
      <div
        className="h-44 relative"
        style={{
          background:
            "linear-gradient(180deg, #B6C8E8 0%, #88A4D5 50%, #5A7BB5 100%), url('https://maps.googleapis.com/dummy')",
        }}
      >
        {/* Pin animation */}
        <div className="absolute top-1/2 left-1/2" style={{ transform: "translate(-50%, -50%)" }}>
          <div
            className="absolute"
            style={{
              width: 40, height: 40, borderRadius: "50%",
              background: "rgba(31,93,198,0.5)",
              transform: "translate(-50%, -50%)",
              animation: "pulse-ring 1.6s ease-out infinite",
            }}
          />
          <MapPin size={32} weight="fill" color="#1F5DC6" style={{ transform: "translate(-50%, -100%)" }} />
        </div>
        {/* Skyline silhouette */}
        <div className="absolute bottom-0 left-0 right-0 flex items-end gap-1 px-4">
          {[40, 70, 55, 85, 50, 95, 60, 75, 45, 80].map((h, i) => (
            <div key={i} style={{ height: h, flex: 1, background: "rgba(14,21,37,0.55)" }} />
          ))}
        </div>
      </div>
      <div className="px-5 py-4">
        <p className="text-[10px] uppercase tracking-[0.25em]" style={{ color: "#5A6478" }}>1280 Tryon St · Charlotte, NC</p>
        <div className="grid grid-cols-3 gap-3 mt-3">
          <Stat label="Loan amount" value="$2.4M" />
          <Stat label="LTV" value="68%" accent />
          <Stat label="DSCR" value="1.42×" accent />
        </div>
      </div>
    </Card>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-[0.2em]" style={{ color: "#5A6478" }}>{label}</div>
      <div className="font-mono text-lg mt-0.5" style={{ color: accent ? "#1F5DC6" : "#0E1525" }}>{value}</div>
    </div>
  );
}

function SyndicationScene() {
  const [pct, setPct] = useState(0);
  useEffect(() => {
    const target = 87;
    let v = 0;
    const t = setInterval(() => {
      v = v >= target ? 0 : Math.min(target, v + 3);
      setPct(v);
    }, 80);
    return () => clearInterval(t);
  }, []);
  const investors = [
    { name: "Pierce Holdings", pct: 35, color: "#1F5DC6" },
    { name: "MeridianStreet", pct: 25, color: "#4086EF" },
    { name: "Vault 88 LLC", pct: 17, color: "#74A8F4" },
    { name: "Open", pct: 13, color: "#E2E5EE" },
  ];
  return (
    <Card style={{ padding: 24 }}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.25em]" style={{ color: "#5A6478" }}>Cap table</p>
          <p className="font-display text-xl mt-0.5" style={{ fontWeight: 400 }}>Bridge · $2.4M</p>
        </div>
        <div className="text-xs px-3 py-1 rounded-full" style={{ background: "#E0F2E8", color: "#1E7D50" }}>
          OPEN
        </div>
      </div>
      <div className="h-3 rounded overflow-hidden flex" style={{ background: "#F0F3F8" }}>
        {investors.slice(0, 3).map((inv) => (
          <div
            key={inv.name}
            style={{
              background: inv.color,
              width: `${(inv.pct / 87) * pct}%`,
              transition: "width 90ms linear",
            }}
          />
        ))}
      </div>
      <div className="flex items-center justify-between mt-2">
        <span className="text-[11px]" style={{ color: "#5A6478" }}>Committed</span>
        <span className="font-mono text-sm" style={{ color: "#0E1525" }}>{pct}%</span>
      </div>
      <div className="mt-4 space-y-1.5">
        {investors.map((inv) => (
          <div key={inv.name} className="flex items-center justify-between text-xs">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full" style={{ background: inv.color }} />
              <span style={{ color: "#0E1525", fontWeight: 500 }}>{inv.name}</span>
            </div>
            <span className="font-mono" style={{ color: "#5A6478" }}>{inv.pct}%</span>
          </div>
        ))}
      </div>
    </Card>
  );
}

function LenderScene() {
  const lenders = [
    { name: "Greystone", score: 92, fits: ["Bridge", "$0–10M", "NC"] },
    { name: "Lima One", score: 78, fits: ["Bridge", "$0–5M"] },
    { name: "Capital One", score: 64, fits: ["Bridge", "$5M+"] },
  ];
  return (
    <Card style={{ padding: 22 }}>
      <p className="text-[10px] uppercase tracking-[0.25em] mb-4" style={{ color: "#5A6478" }}>
        Lender match · Bridge · $2.4M · NC
      </p>
      {lenders.map((l, i) => (
        <div
          key={l.name}
          className="flex items-center gap-3 py-2"
          style={{ borderBottom: i < lenders.length - 1 ? "1px solid #E2E5EE" : "none" }}
        >
          <div
            className="w-9 h-9 rounded-full flex items-center justify-center"
            style={{ background: i === 0 ? "#E0F2E8" : "#E7EEFB" }}
          >
            <Bank size={16} color={i === 0 ? "#1E7D50" : "#1F5DC6"} weight="duotone" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm" style={{ fontWeight: 500 }}>{l.name}</div>
            <div className="flex flex-wrap gap-1 mt-0.5">
              {l.fits.map((f) => (
                <span key={f} className="text-[9px] px-1.5 py-0.5 rounded-full" style={{ background: "#E7EEFB", color: "#1F5DC6" }}>{f}</span>
              ))}
            </div>
          </div>
          <div className="text-right">
            <div className="font-mono text-lg" style={{ color: i === 0 ? "#1E7D50" : "#0E1525", fontWeight: 500 }}>{l.score}</div>
            <div className="text-[9px] uppercase tracking-[0.2em]" style={{ color: "#5A6478" }}>match</div>
          </div>
        </div>
      ))}
    </Card>
  );
}

function CommissionScene() {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    setProgress(0);
    const t = setInterval(() => {
      setProgress((p) => (p >= 100 ? 0 : p + 5));
    }, 80);
    return () => clearInterval(t);
  }, []);
  const sent = progress >= 95;
  return (
    <Card style={{ padding: 24 }}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-[10px] uppercase tracking-[0.25em]" style={{ color: "#5A6478" }}>Payout · ACH</p>
          <div className="font-display text-2xl mt-0.5" style={{ fontWeight: 400 }}>$36,250.00</div>
        </div>
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center transition-all"
          style={{
            background: sent ? "#1E7D50" : "#E7EEFB",
            transform: sent ? "scale(1.05)" : "scale(1)",
          }}
        >
          <CheckCircle size={20} color={sent ? "#FFFFFF" : "#1F5DC6"} weight={sent ? "fill" : "duotone"} />
        </div>
      </div>
      <div className="h-1.5 rounded overflow-hidden" style={{ background: "#E2E5EE" }}>
        <div
          className="h-full"
          style={{ width: `${progress}%`, background: "#1F5DC6", transition: "width 90ms linear" }}
        />
      </div>
      <div className="flex items-center justify-between mt-2 mb-4">
        <span className="text-[11px]" style={{ color: "#5A6478" }}>{sent ? "Sent · ref ACH-184329" : "Sending…"}</span>
        <span className="text-[11px] font-mono" style={{ color: "#5A6478" }}>{progress}%</span>
      </div>
      <div className="grid grid-cols-3 gap-3 pt-3" style={{ borderTop: "1px solid #E2E5EE" }}>
        <div>
          <div className="text-[9px] uppercase tracking-[0.2em]" style={{ color: "#5A6478" }}>MTD</div>
          <div className="font-mono text-sm mt-0.5">$36,250</div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-[0.2em]" style={{ color: "#5A6478" }}>YTD</div>
          <div className="font-mono text-sm mt-0.5">$284,910</div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-[0.2em]" style={{ color: "#5A6478" }}>Lifetime</div>
          <div className="font-mono text-sm mt-0.5">$1.6M</div>
        </div>
      </div>
    </Card>
  );
}
